import React from 'react';

/**
 * Highlights search terms within text by wrapping them in styled spans
 * Supports multiple search terms separated by spaces
 *
 * @param text - The text to search within
 * @param searchTerm - The search term(s) to highlight (space-separated for multiple terms)
 * @returns React node with highlighted search terms
 *
 * @example
 * highlightSearchTerm("Hello world", "world") // Returns "Hello <span>world</span>"
 * highlightSearchTerm("Hello world", "hello world") // Highlights both words
 */
export const highlightSearchTerm = (
  text: string | React.ReactNode,
  searchTerm?: string,
): React.ReactNode => {
  if (!searchTerm || typeof text !== 'string') return text;

  try {
    // Handle multiple words in search term
    const searchTerms = searchTerm
      .trim()
      .split(/\s+/)
      .filter((term) => term.length > 0);

    // If no valid search terms, return the original text
    if (searchTerms.length === 0) return text;

    // Create a single regex pattern with all search terms as alternatives
    const escapedTerms = searchTerms.map((term) =>
      term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );

    const pattern = escapedTerms.join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    // Split the text by the search terms, keeping the delimiters
    const parts = text.split(regex);

    // If no matches found (split returns single element), return original text
    if (parts.length === 1) return text;

    // Build the result, highlighting every odd-indexed part (the matches)
    return parts.map((part, i) => {
      // Even indices are non-matches, odd indices are matches
      if (i % 2 === 0) {
        return part;
      }
      // This is a match, highlight it
      return (
        <span
          key={i}
          style={{
            backgroundColor: '#fff176',
          }}
        >
          {part}
        </span>
      );
    });
  } catch (error) {
    console.error('Search highlighting error:', error);
    return text;
  }
};
